import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loading, NativeSelect, Confirm } from "@/components/common";
import { api, errMsg } from "@/lib/api";

function RoleDialog({ open, onOpenChange, initial, roles, onSave }) {
  const [f, setF] = useState({ label: "", description: "", copy_from: "" });
  useEffect(() => {
    if (open) setF(initial ? { label: initial.label, description: initial.description || "", copy_from: "" } : { label: "", description: "", copy_from: "" });
  }, [open, initial]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{initial ? "Ganti Nama Level" : "Level Akses Baru"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...f, copy_from: f.copy_from || null }); }} className="space-y-3" data-testid="role-form">
          <div><Label>Nama Level</Label><Input value={f.label} onChange={set("label")} placeholder="mis. Kasir" required maxLength={40} data-testid="role-label-input" /></div>
          <div><Label>Keterangan (opsional)</Label><Input value={f.description} onChange={set("description")} maxLength={120} data-testid="role-desc-input" /></div>
          {!initial && (
            <div><Label>Hak akses awal</Label>
              <NativeSelect value={f.copy_from} onChange={set("copy_from")} className="w-full h-11" data-testid="role-copy-select">
                <option value="">Mulai dari kosong</option>
                {roles.map((r) => <option key={r.key} value={r.key}>Salin dari {r.label}</option>)}
              </NativeSelect>
            </div>
          )}
          <DialogFooter><Button type="submit" className="h-11 bg-emerald-700 hover:bg-emerald-800" data-testid="role-form-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RoleHeader({ r, manage, onEdit, onDelete }) {
  return (
    <th className="text-center min-w-[110px]" title={r.description || ""}>
      <div>{r.label}</div>
      {r.custom && <span className="inline-block mt-0.5 text-[9px] font-semibold uppercase px-1.5 rounded-sm bg-violet-100 text-violet-800" data-testid={`role-custom-badge-${r.key}`}>Kustom</span>}
      {manage && r.custom && (
        <div className="flex justify-center gap-0.5 mt-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(r)} data-testid={`role-rename-${r.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Confirm title={`Hapus level ${r.label}?`} description="Hanya bisa jika tidak ada pengguna di level ini." onConfirm={() => onDelete(r)}
            trigger={<Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" data-testid={`role-delete-${r.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>} />
        </div>
      )}
    </th>
  );
}

export function PermMatrix({ url, manageCustom = false }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const apply = (d, keep) => {
    setData(d);
    setSel(Object.fromEntries(d.roles.map((r) => [r.key, keep?.[r.key] || new Set(d.current[r.key])])));
  };
  const load = useCallback(async () => {
    try { apply((await api.get(url)).data); } catch (e) { toast.error(errMsg(e)); }
  }, [url]);
  useEffect(() => {
    load();
  }, [load]);
  if (!data) return <Loading />;
  const toggle = (role, key) => setSel((s) => {
    const n = new Set(s[role]);
    if (n.has(key)) n.delete(key); else n.add(key);
    return { ...s, [role]: n };
  });
  const dirty = data.roles.some((r) => JSON.stringify([...sel[r.key]].sort()) !== JSON.stringify(data.current[r.key]));
  const save = async () => {
    try {
      apply((await api.put(url, { roles: Object.fromEntries(data.roles.map((r) => [r.key, [...sel[r.key]]])) })).data);
      toast.success("Hak akses disimpan & tercatat di audit log");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = () => setSel(Object.fromEntries(data.roles.map((r) => [r.key, new Set(data.defaults[r.key])])));
  const saveRole = async (f) => {
    try {
      const res = dlg.initial ? await api.put(`/roles/custom/${dlg.initial.key}`, f) : await api.post("/roles/custom", f);
      apply(res.data, sel);
      toast.success(dlg.initial ? "Nama level diganti" : `Level ${f.label} dibuat`);
      setDlg({ open: false, initial: null });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delRole = async (r) => {
    try {
      apply((await api.delete(`/roles/custom/${r.key}`)).data, sel);
      toast.success(`Level ${r.label} dihapus`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const customCount = data.roles.filter((r) => r.custom).length;
  return (
    <div className="space-y-3" data-testid="perm-matrix">
      <div className="flex flex-wrap gap-2 justify-end">
        {manageCustom && <Button variant="outline" onClick={() => setDlg({ open: true, initial: null })} disabled={customCount >= data.max_custom} className="h-11 sm:h-10" data-testid="role-add-btn"><Plus className="h-4 w-4 mr-2" />Level Baru</Button>}
        <Button variant="outline" onClick={reset} className="h-11 sm:h-10" data-testid="perm-reset-btn"><RotateCcw className="h-4 w-4 mr-2" />Kembalikan ke Bawaan</Button>
        <Button onClick={save} disabled={!dirty} className="h-11 sm:h-10 bg-emerald-700 hover:bg-emerald-800" data-testid="perm-save-btn"><Save className="h-4 w-4 mr-2" />Simpan</Button>
      </div>
      <div className="xl-wrap max-h-[65vh]">
        <table className="xl">
          <thead><tr><th className="xl-corner xl-wrap-cell">Fitur / Tindakan</th>{data.roles.map((r) => <RoleHeader key={r.key} r={r} manage={manageCustom} onEdit={(x) => setDlg({ open: true, initial: x })} onDelete={delRole} />)}</tr></thead>
          <tbody>
            {data.catalog.map((c) => (
              <tr key={c.key}>
                <td className="sticky left-0 bg-white z-[1] xl-wrap-cell">{c.label}{!c.grantable && <span className="ml-2 text-[10px] text-slate-500">(khusus Pemilik)</span>}</td>
                {data.roles.map((r) => (
                  <td key={r.key} className="text-center">
                    <Checkbox checked={sel[r.key].has(c.key)} disabled={!c.grantable} onCheckedChange={() => toggle(r.key, c.key)} className="h-5 w-5" data-testid={`perm-${r.key}-${c.key.replace(":", "-")}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Pemilik selalu memiliki akses penuh. Perubahan berlaku untuk semua pengguna level tersebut.{manageCustom && ` Level kustom: ${customCount}/${data.max_custom}.`}</p>
      {manageCustom && <RoleDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} initial={dlg.initial} roles={data.roles} onSave={saveRole} />}
    </div>
  );
}
